/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 1on1 API クライアント。/one-on-one/* (ペア取得・開設・共有相手更新・タイムライン・セッション更新) を呼び出し
 * @graph-connects /one-on-one/pairs [calls] ペア取得・開設 API
 * @graph-connects /one-on-one/pairs/participants [calls] 共有相手更新 API
 * @graph-connects /one-on-one/pairs/sessions [calls] タイムライン取得・セッション更新 API
 */
import type {
  CreateOneOnOnePairRequest,
  OneOnOnePairLookupResponse,
  OneOnOnePairResponse,
  OneOnOneSessionListResponse,
  OneOnOneSessionResponse,
  UpdateOneOnOneParticipantsRequest,
  UpdateOneOnOneSessionRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { request } from './_request.js';

/**
 * 1on1 のペア・タイムラインを扱う API クライアント。契約は
 * `@cortex/domain-product-innovation-lab-types` を単一 SoT とする。
 * @graph-connects none
 */
export const oneOnOneApi = {
  getPairByMember: (memberEmail: string): Promise<OneOnOnePairLookupResponse> => {
    return request<OneOnOnePairLookupResponse>(
      `/one-on-one/pairs?memberEmail=${encodeURIComponent(memberEmail)}`,
    );
  },
  createPair: (body: CreateOneOnOnePairRequest): Promise<OneOnOnePairResponse> => {
    return request<OneOnOnePairResponse>('/one-on-one/pairs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updateParticipants: (
    pairId: string,
    body: UpdateOneOnOneParticipantsRequest,
  ): Promise<OneOnOnePairResponse> => {
    return request<OneOnOnePairResponse>(
      `/one-on-one/pairs/${encodeURIComponent(pairId)}/participants`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  },
  listSessions: (
    pairId: string,
    params: { limit?: number; before?: string } = {},
  ): Promise<OneOnOneSessionListResponse> => {
    const search = new URLSearchParams();
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.before !== undefined) search.set('before', params.before);
    const query = search.toString();
    return request<OneOnOneSessionListResponse>(
      `/one-on-one/pairs/${encodeURIComponent(pairId)}/sessions${query ? `?${query}` : ''}`,
    );
  },
  updateSession: (
    pairId: string,
    sessionId: string,
    body: UpdateOneOnOneSessionRequest,
  ): Promise<OneOnOneSessionResponse> => {
    return request<OneOnOneSessionResponse>(
      `/one-on-one/pairs/${encodeURIComponent(pairId)}/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  },
  /** 保存済みの文字起こしから振り返りを作り直す（リーダー / admin） */
  regenerateLeaderFeedback: (
    pairId: string,
    sessionId: string,
  ): Promise<OneOnOneSessionResponse> => {
    return request<OneOnOneSessionResponse>(
      `/one-on-one/pairs/${encodeURIComponent(pairId)}/sessions/${encodeURIComponent(sessionId)}/leader-feedback/regenerate`,
      { method: 'POST' },
    );
  },
};
